/**
 * Realtime Provider - Supabase Realtime Subscriptions
 *
 * Centralizes all Supabase Realtime subscriptions and automatically
 * invalidates React Query caches when data changes.
 *
 * Replaces polling with real-time updates for:
 * - Presence/team status
 * - Blockers
 * - Notifications
 * - EOD submissions
 */

import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/contexts/AuthContext';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface RealtimeProviderProps {
  children: React.ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const channelsRef = useRef<RealtimeChannel[]>([]);

  useEffect(() => {
    if (!user) return;

    // Clean up any existing subscriptions
    channelsRef.current.forEach((channel) => {
      supabase.removeChannel(channel);
    });
    channelsRef.current = [];

    // ==========================================================================
    // 1. Presence Changes (work_sessions, work_status_log)
    // ==========================================================================
    const presenceChannel = supabase
      .channel('presence-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'work_sessions',
        },
        (payload) => {
          console.log('[Realtime] Work session change:', payload.eventType);
          // PERFORMANCE: Invalidate specific queries, not entire cache branches
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.status() });
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.team() });
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.combined() });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'work_status_log',
        },
        (payload) => {
          console.log('[Realtime] Status change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.status() });
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.team() });
        }
      );

    // ==========================================================================
    // 2. Blockers
    // ==========================================================================
    const blockersChannel = supabase
      .channel('blockers-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'blockers',
        },
        (payload) => {
          console.log('[Realtime] Blocker change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.blockers() });
          queryClient.invalidateQueries({ queryKey: queryKeys.presence.combined() });
        }
      );

    // ==========================================================================
    // 3. Notifications (for current user)
    // ==========================================================================
    const notificationsChannel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[Realtime] New notification:', payload.new);
          // PERFORMANCE: Only invalidate notifications, not entire bootstrap cache
          queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[Realtime] Notification updated:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
        }
      );

    // ==========================================================================
    // 4. Agency Intel (action_items, meetings, slack_messages)
    // ==========================================================================
    const intelChannel = supabase
      .channel('intel-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'action_items',
        },
        (payload) => {
          console.log('[Realtime] Action item change:', payload.eventType);
          // PERFORMANCE: Invalidate specific intel queries, not all
          queryClient.invalidateQueries({ queryKey: queryKeys.intel.feed() });
          queryClient.invalidateQueries({ queryKey: queryKeys.intel.stats() });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'meetings',
        },
        (payload) => {
          console.log('[Realtime] New meeting:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: queryKeys.intel.feed() });
          queryClient.invalidateQueries({ queryKey: queryKeys.intel.stats() });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'slack_messages',
          filter: 'is_potential_action=eq.true',
        },
        (payload) => {
          console.log('[Realtime] Slack message with action:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: queryKeys.intel.feed() });
          queryClient.invalidateQueries({ queryKey: queryKeys.intel.pendingActions() });
        }
      );

    // ==========================================================================
    // 5. Huddles (Video/Audio Calls) - Real-time invite notifications
    // NOTE: No filter on huddle_participants - filter client-side for reliability
    // (Service role inserts may not trigger filtered subscriptions)
    // ==========================================================================
    const huddlesChannel = supabase
      .channel('huddles-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'huddle_participants',
        },
        (payload) => {
          // Filter client-side: only invalidate if this change affects current user
          const newData = payload.new as { user_id?: string } | undefined;
          const oldData = payload.old as { user_id?: string } | undefined;
          const affectsUser = newData?.user_id === user.id || oldData?.user_id === user.id;

          if (affectsUser) {
            console.log('[Realtime] Huddle participant change for current user:', payload.eventType, payload.new);
            queryClient.invalidateQueries({ queryKey: queryKeys.huddles.pendingInvites() });
            queryClient.invalidateQueries({ queryKey: queryKeys.huddles.active() });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'huddles',
        },
        (payload) => {
          console.log('[Realtime] Huddle change:', payload.eventType);
          // Invalidate when any huddle changes (ended, active, etc.)
          queryClient.invalidateQueries({ queryKey: queryKeys.huddles.active() });
          queryClient.invalidateQueries({ queryKey: queryKeys.huddles.pendingInvites() });
        }
      );

    // ==========================================================================
    // 6. LiquorChat Tickets (real-time Kanban updates)
    // ==========================================================================
    const lcTicketsChannel = supabase
      .channel('lc-tickets-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lc_tickets',
        },
        (payload) => {
          console.log('[Realtime] LC ticket change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['liquorchat', 'tickets'] });
        }
      );

    // Subscribe to all channels
    // PERFORMANCE: Removed eodChannel and invitesChannel - not needed in realtime
    const channels = [
      presenceChannel,
      blockersChannel,
      notificationsChannel,
      intelChannel,
      huddlesChannel,
      lcTicketsChannel,
    ];

    channels.forEach((channel) => {
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`[Realtime] Subscribed to ${channel.topic}`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`[Realtime] Error subscribing to ${channel.topic}`);
        }
      });
    });

    channelsRef.current = channels;

    // Cleanup on unmount
    return () => {
      console.log('[Realtime] Cleaning up subscriptions');
      channels.forEach((channel) => {
        supabase.removeChannel(channel);
      });
      channelsRef.current = [];
    };
  }, [user, queryClient]);

  return <>{children}</>;
}

export default RealtimeProvider;
